import { useMemo, useState } from 'react'
import { useNotificationsContext } from '../context/NotificationsContext'
import './Notifications.scss'

// ── Helpers de fecha ──────────────────────────────────────────────
const toDateKey = dateStr => {
  if (!dateStr) return 'Sin fecha'
  const d = new Date(dateStr)
  if (isNaN(d)) return 'Sin fecha'
  return d.toISOString().slice(0, 10) // "YYYY-MM-DD"
}

const formatGroupLabel = key => {
  if (key === 'Sin fecha') return key
  const d = new Date(key)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  if (sameDay(d, today)) return 'Hoy'
  if (sameDay(d, yesterday)) return 'Ayer'

  return d.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  })
}

const formatTime = dateStr => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d)) return ''
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
}

// ── Componente principal ──────────────────────────────────────────
export const Notifications = () => {
  const {
    notifications,
    readNotifications,
    unreadCount,
    markOneAsRead,
    markAllAsRead,
  } = useNotificationsContext()

  const [dismissing, setDismissing] = useState([])

  const handleDismiss = id => {
    setDismissing(prev => [...prev, id])
    setTimeout(() => {
      markOneAsRead(id)
      setDismissing(prev => prev.filter(d => d !== id))
    }, 300)
  }

  const handleDismissAll = () => {
    const ids = notifications.map(n => n._id).filter(Boolean)
    setDismissing(ids)
    setTimeout(() => {
      markAllAsRead()
      setDismissing([])
    }, 320)
  }

  // Mezclar y ordenar por fecha desc, luego agrupar
  const groups = useMemo(() => {
    const all = [
      ...notifications.map(n => ({ ...n, isRead: false })),
      ...readNotifications.map(n => ({ ...n, isRead: true })),
    ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

    const map = new Map()
    for (const notif of all) {
      const key = toDateKey(notif.createdAt)
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(notif)
    }
    return Array.from(map.entries()) // [[key, [notifs]], ...]
  }, [notifications, readNotifications])

  const isEmpty = notifications.length === 0 && readNotifications.length === 0

  return (
    <div className="notif-view">
      {/* Header */}
      <div className="notif-view__header">
        <div className="notif-view__title-row">
          <h1 className="notif-view__title">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            Notificaciones
            {unreadCount > 0 && (
              <span className="notif-view__badge">{unreadCount}</span>
            )}
          </h1>
          {unreadCount > 0 && (
            <button
              className="notif-view__dismiss-all"
              onClick={handleDismissAll}
            >
              Descartar todas
            </button>
          )}
        </div>
        {unreadCount > 0 && (
          <p className="notif-view__subtitle">
            Tienes {unreadCount} notificación{unreadCount !== 1 ? 'es' : ''} sin
            leer
          </p>
        )}
      </div>

      {/* Grupos */}
      {isEmpty ? (
        <div className="notif-view__empty">
          <svg
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          <p>Todo al día — sin notificaciones pendientes</p>
        </div>
      ) : (
        <div className="notif-view__groups">
          {groups.map(([key, items]) => (
            <div key={key} className="notif-group">
              <div className="notif-group__label">
                <span>{formatGroupLabel(key)}</span>
              </div>
              <div className="notif-group__list">
                {items.map((notif, i) => (
                  <NotifCard
                    key={notif._id || i}
                    notif={notif}
                    time={formatTime(notif.createdAt)}
                    dismissing={!notif.isRead && dismissing.includes(notif._id)}
                    onDismiss={() => handleDismiss(notif._id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Card individual ───────────────────────────────────────────────
const NotifCard = ({ notif, time, dismissing, onDismiss }) => (
  <div
    className={[
      'notif-card',
      notif.isRead ? 'notif-card--read' : 'notif-card--unread',
      dismissing ? 'notif-card--dismissing' : '',
    ]
      .filter(Boolean)
      .join(' ')}
  >
    <div className="notif-card__dot" />
    <div className="notif-card__body">
      <div className="notif-card__header">
        <p className="notif-card__title">{notif.title}</p>
        {time && <span className="notif-card__time">{time}</span>}
      </div>
      <div
        className="notif-card__content"
        dangerouslySetInnerHTML={{ __html: notif.content }}
      />
    </div>
    {!notif.isRead && notif._id && (
      <button
        className="notif-card__dismiss"
        onClick={onDismiss}
        aria-label="Descartar notificación"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    )}
  </div>
)
